import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { ArrowLeft, MapPin, Calendar, Clock, IndianRupee, Briefcase, User, Phone, Star } from 'lucide-react';
import RatingInput from '../RatingInput';

const BRAND_COLORS = {
    primary: '#1a1a1a',
    accent: '#C9A961',
    light: '#F5F5F0',
    lightGray: '#E8E8E3',
    success: '#10B981',
    danger: '#EF4444',
};

const JobDetails = ({ user }) => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [employer, setEmployer] = useState(null);
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '']);
    const [otpType, setOtpType] = useState('start'); // 'start' or 'end'

    const [submittingRating, setSubmittingRating] = useState(false);
    const [existingRating, setExistingRating] = useState(null);

    // Re-implementing fetch logic with proper imports
    useEffect(() => {
        const loadData = async () => {
            if (!user || !jobId) return;

            try {
                // 1. Get Job
                const jobRef = doc(db, 'jobs', jobId);
                const jobSnap = await getDoc(jobRef);

                if (!jobSnap.exists()) {
                    setError('Job not found');
                    return;
                }
                const jobData = { id: jobSnap.id, ...jobSnap.data() };
                setJob(jobData);

                // 2. Get Employer Details
                if (jobData.employerId) {
                    try {
                        const empRef = doc(db, 'employers', jobData.employerId);
                        const empSnap = await getDoc(empRef);
                        if (empSnap.exists()) {
                            setEmployer({ id: empSnap.id, ...empSnap.data() });
                        }
                    } catch (empErr) {
                        console.error("Error fetching employer:", empErr);
                    }
                }

                // 3. Get Application
                // Fetch directly from job's applications subcollection using professionalId (user.uid) as key
                const appRef = doc(db, 'jobs', jobId, 'applications', user.uid);
                const appSnap = await getDoc(appRef);

                if (appSnap.exists()) {
                    const appData = { id: appSnap.id, ...appSnap.data() };
                    setApplication(appData);

                    // Check for existing rating if job is completed
                    if (appData.detailedStatus === 'job_completed' && jobData.employerId) {
                        const ratingsRef = collection(db, 'employers', jobData.employerId, 'ratings');
                        const q = query(ratingsRef, where('jobId', '==', jobId));
                        const snapshot = await getDocs(q);
                        if (!snapshot.empty) {
                            setExistingRating({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
                        }
                    }
                } else {
                    console.log('No application found for this job');
                }
            } catch (err) {
                console.error(err);
                setError('Error loading data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user, jobId]);

    const handleSubmitRating = async (ratingData) => {
        if (!job || !user) return;
        setSubmittingRating(true);
        try {
            const ratingPayload = {
                ...ratingData,
                jobId: job.id,
                reviewerId: user.uid,
                reviewerRole: 'professional',
                createdAt: serverTimestamp(),
            };

            await addDoc(collection(db, 'employers', job.employerId, 'ratings'), ratingPayload);

            setExistingRating(ratingPayload);
        } catch (error) {
            console.error("Error submitting rating:", error);
            // setError("Failed to submit rating");
        } finally {
            setSubmittingRating(false);
        }
    };



    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: BRAND_COLORS.accent }}></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen p-6 flex flex-col items-center justify-center bg-white">
                <p className="text-red-500 mb-4">{error}</p>
                <button onClick={() => navigate('/professional/home')} className="text-blue-500 underline">
                    Go Home
                </button>
            </div>
        );
    }

    const handleReachedLocation = async () => {
        setProcessing(true);
        try {
            // Get Location
            if (!navigator.geolocation) {
                throw new Error("Geolocation is not supported by this browser.");
            }

            navigator.geolocation.getCurrentPosition(async (position) => {
                try {
                    const { latitude, longitude } = position.coords;
                    const markReached = httpsCallable(functions, 'markReachedLocation');
                    await markReached({
                        jobId: jobId,
                        location: { lat: latitude, lng: longitude }
                    });

                    // Update local state
                    setApplication(prev => ({
                        ...prev,
                        detailedStatus: 'reached_location'
                    }));
                    setMessage("Location updated! You can now start the job.");
                } catch (err) {
                    console.error(err);
                    setError("Failed to update location.");
                } finally {
                    setProcessing(false);
                }
            }, (err) => {
                console.error(err);
                setError("Unable to retrieve location. Please enable location services.");
                setProcessing(false);
            });

        } catch (err) {
            console.error(err);
            setError(err.message);
            setProcessing(false);
        }
    };

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;

        setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

        // Focus next input
        if (element.nextSibling && element.value !== "") {
            element.nextSibling.focus();
        }
    };

    const handleOtpSubmit = async () => {
        const otpValue = otp.join("");
        if (otpValue.length !== 4) {
            setError("Please enter a valid 4-digit OTP");
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const functionName = otpType === 'start' ? 'startJobWithOtp' : 'completeJobWithOtp';
            const actionFunc = httpsCallable(functions, functionName);

            const result = await actionFunc({
                jobId: jobId,
                otp: otpValue
            });

            if (result.data.success) {
                setShowOtpModal(false);
                setOtp(['', '', '', '']); // Reset OTP
                setMessage(result.data.message);

                // Update local state
                const newStatus = otpType === 'start' ? 'job_started' : 'job_completed';
                setApplication(prev => ({
                    ...prev,
                    detailedStatus: newStatus
                }));
            }
        } catch (err) {
            console.error(err);
            setError(err.message || "Invalid OTP or Server Error");
        } finally {
            setProcessing(false);
        }
    };

    const handleAction = async (action) => {
        if (!application) return;

        if (action === 'approve') {
            const confirmed = window.confirm("Important: You must reach the job location 30 minutes prior to the start time for preparation. Do you agree?");
            if (!confirmed) return;
        }

        setProcessing(true);
        setError(null);

        try {
            const respondToJob = httpsCallable(functions, 'respondToJobInvitation');

            const result = await respondToJob({
                jobId: jobId,
                applicationId: application.id,
                action: action
            });

            if (result.data.success) {
                setMessage(result.data.message);
                // Refresh application state
                setApplication(prev => ({ ...prev, status: action === 'approve' ? 'accepted' : 'rejected' }));

                if (action === 'approve') {
                    // Stay on page to proceed with flow
                    // navigate('/professional/home'); // Removed navigate
                } else {
                    setTimeout(() => navigate('/professional/home'), 2000);
                }
            } else {
                setError(result.data.message || 'Operation failed');
                if (result.data.reason === 'already_allocated') {
                    setApplication(prev => ({ ...prev, status: 'withdrawn' }));
                }
            }
        } catch (err) {
            console.error('Action failed:', err);
            setError(err.message || 'Failed to process request');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {/* Header */}
            <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
                <div className="max-w-3xl mx-auto flex items-center">
                    <button onClick={() => navigate('/professional/home')} className="mr-4 p-2 hover:bg-gray-100 rounded-full">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-medium">Job Details</h1>
                </div>
            </div>

            <div className="max-w-3xl mx-auto p-4 mt-4">
                {message && (
                    <div className={`p-4 mb-6 rounded-lg ${message.includes('success') || message.includes('accepted') ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                        {message}
                    </div>
                )}

                {/* Job Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                    <div className="p-6">
                        <h2 className="text-2xl font-light mb-2" style={{ color: BRAND_COLORS.primary }}>{job?.title}</h2>
                        <div className="flex items-center text-gray-500 mb-6">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job?.location?.lat && job?.location?.lng ? (
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${job.location.lat},${job.location.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm hover:underline text-blue-600"
                                >
                                    {job?.location?.address || 'View on Map'}
                                </a>
                            ) : (
                                <span className="text-sm">{job?.location?.address}</span>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center text-gray-500 mb-1">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <span className="text-xs uppercase tracking-wider">Date</span>
                                </div>
                                <p className="font-medium">{job?.date}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center text-gray-500 mb-1">
                                    <Clock className="w-4 h-4 mr-2" />
                                    <span className="text-xs uppercase tracking-wider">Time</span>
                                </div>
                                <p className="font-medium">{job?.time}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center text-gray-500 mb-1">
                                    <IndianRupee className="w-4 h-4 mr-2" />
                                    <span className="text-xs uppercase tracking-wider">Budget</span>
                                </div>
                                <p className="font-medium">₹{job?.budget?.value}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center text-gray-500 mb-1">
                                    <Briefcase className="w-4 h-4 mr-2" />
                                    <span className="text-xs uppercase tracking-wider">Duration</span>
                                </div>
                                <p className="font-medium">{job?.duration} Hours</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Description</h3>
                            <p className="text-gray-600 leading-relaxed">{job?.description}</p>
                        </div>

                        <div className="border-t pt-6">
                            <h3 className="text-sm font-medium text-gray-900 mb-4">Employer</h3>
                            <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 overflow-hidden bg-gray-200 border border-gray-100">
                                    {employer?.profilePicture?.url ? (
                                        <img
                                            src={employer.profilePicture.url.replace('10.0.2.2', 'localhost')}
                                            alt={employer.displayName || job?.employerName}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-5 h-5 text-gray-500" />
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium">{employer?.displayName || job?.employerName || 'SkillVend Employer'}</p>
                                    {/* Only show phone if accepted */}
                                    {application?.status === 'accepted' && (
                                        <div className="flex items-center text-green-600 mt-1">
                                            <Phone className="w-3 h-3 mr-1" />
                                            <span className="text-sm">
                                                {employer?.phoneNumber || job?.employerPhone || 'Contact available'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                {application?.status === 'invited' && (
                    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 flex space-x-4 max-w-3xl mx-auto w-full">
                        <button
                            onClick={() => handleAction('reject')}
                            disabled={processing}
                            className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                        >
                            Reject
                        </button>
                        <button
                            onClick={() => handleAction('approve')}
                            disabled={processing}
                            className="flex-1 py-3 px-4 rounded-lg text-white font-medium hover:opacity-90 disabled:opacity-50 shadow-lg"
                            style={{ backgroundColor: BRAND_COLORS.primary }}
                        >
                            {processing ? 'Processing...' : 'Accept Job'}
                        </button>
                    </div>
                )}

                {application?.status === 'accepted' && (
                    <>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center mb-4">
                            <p className="text-green-800 font-medium">
                                {application.detailedStatus === 'job_completed' ? 'Job Completed' :
                                    application.detailedStatus === 'job_started' ? 'Job In Progress' :
                                        'You have accepted this job'}
                            </p>
                        </div>

                        {/* Status Progression UI */}
                        <div className="mb-6 px-4">
                            <div className="flex justify-between text-xs text-gray-500 mb-2">
                                <span>Accepted</span>
                                <span>Reached</span>
                                <span>Started</span>
                                <span>Completed</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-500 transition-all duration-500"
                                    style={{
                                        width: application.detailedStatus === 'job_completed' ? '100%' :
                                            application.detailedStatus === 'job_started' ? '75%' :
                                                application.detailedStatus === 'reached_location' ? '50%' : '25%'
                                    }}
                                ></div>
                            </div>
                        </div>

                        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 flex flex-col space-y-3 max-w-3xl mx-auto w-full z-20">

                            {/* Reached Location Button */}
                            {['accepted_by_professional', 'fully_approved', 'partially_approved'].includes(application.detailedStatus) && (
                                <button
                                    onClick={handleReachedLocation}
                                    disabled={processing}
                                    className="w-full py-3 px-4 rounded-lg text-white font-medium hover:opacity-90 shadow-lg flex items-center justify-center bg-blue-600"
                                >
                                    <MapPin className="w-5 h-5 mr-2" />
                                    {processing ? 'Updating...' : 'I Have Reached Location'}
                                </button>
                            )}

                            {/* Start Job Button */}
                            {application.detailedStatus === 'reached_location' && (
                                <button
                                    onClick={() => { setOtpType('start'); setShowOtpModal(true); }}
                                    className="w-full py-3 px-4 rounded-lg text-white font-medium hover:opacity-90 shadow-lg flex items-center justify-center bg-green-600"
                                >
                                    <Clock className="w-5 h-5 mr-2" />
                                    Start Job (Enter OTP)
                                </button>
                            )}

                            {/* End Job Button */}
                            {application.detailedStatus === 'job_started' && (
                                <button
                                    onClick={() => { setOtpType('end'); setShowOtpModal(true); }}
                                    className="w-full py-3 px-4 rounded-lg text-white font-medium hover:opacity-90 shadow-lg flex items-center justify-center bg-purple-600"
                                >
                                    <Briefcase className="w-5 h-5 mr-2" />
                                    Complete Job (Enter End OTP)
                                </button>
                            )}

                            <a
                                href={`tel:${job?.employerPhone}`}
                                className="w-full py-3 px-4 rounded-lg bg-gray-100 text-gray-800 font-medium hover:bg-gray-200 flex items-center justify-center"
                            >
                                <Phone className="w-5 h-5 mr-2" />
                                Call Employer
                            </a>
                        </div>
                    </>
                )}

                {application?.status === 'rejected' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <p className="text-red-800 font-medium">You have rejected this job</p>
                    </div>
                )}

                {application?.status === 'withdrawn' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                        <p className="text-yellow-800 font-medium">Opportunity Lost - Job allocated to another professional</p>
                    </div>
                )}

                {/* Spacer for fixed bottom bar */}
                <div className="h-40"></div>

                {/* Rating Section - Only for Completed Jobs */}
                {application?.detailedStatus === 'job_completed' && (
                    <div className="max-w-3xl mx-auto px-4 mb-24">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            {existingRating ? (
                                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-100">
                                    <h4 className="font-bold text-lg text-yellow-800 mb-3 flex items-center">
                                        <Star className="w-5 h-5 mr-2 fill-yellow-600 text-yellow-600" />
                                        You rated this Employer
                                    </h4>
                                    <div className="flex items-center gap-1 mb-3">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <Star
                                                key={star}
                                                size={20}
                                                className={`${existingRating.rating >= star ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
                                            />
                                        ))}
                                        <span className="ml-2 font-medium text-yellow-900">{existingRating.rating}.0</span>
                                    </div>
                                    {existingRating.pills && existingRating.pills.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {existingRating.pills.map((pill, i) => (
                                                <span key={i} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full border border-yellow-200">
                                                    {pill}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {existingRating.comment ? (
                                        <p className="text-gray-700 italic border-l-4 border-yellow-200 pl-3 py-1">"{existingRating.comment}"</p>
                                    ) : null}
                                </div>
                            ) : (
                                <RatingInput
                                    role="employer"
                                    onSubmit={handleSubmitRating}
                                    submitting={submittingRating}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* OTP Modal */}
            {showOtpModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-sm">
                        <h3 className="text-lg font-medium mb-4 text-center">
                            {otpType === 'start' ? 'Enter Start Job OTP' : 'Enter End Job OTP'}
                        </h3>
                        <p className="text-sm text-gray-500 text-center mb-6">
                            Ask the employer for the {otpType === 'start' ? 'Start' : 'End'} OTP to proceed.
                        </p>

                        <div className="flex justify-center gap-2 mb-6">
                            {otp.map((data, index) => {
                                return (
                                    <input
                                        className="w-12 h-12 border-2 rounded bg-transparent outline-none text-center font-semibold text-xl border-gray-200 focus:border-black focus:text-black transition-colors"
                                        type="text"
                                        name="otp"
                                        maxLength="1"
                                        key={index}
                                        value={data}
                                        onChange={e => handleOtpChange(e.target, index)}
                                        onFocus={e => e.target.select()}
                                    />
                                );
                            })}
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowOtpModal(false)}
                                className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleOtpSubmit}
                                disabled={processing}
                                className="flex-1 py-2 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                            >
                                {processing ? 'Verifying...' : (otpType === 'start' ? 'Verify & Start' : 'Verify & Complete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobDetails;
